import { useEffect, useState } from "react";
import FEPaginatedTable from "@components/Tables/FEPaginatedTable/FEPaginatedTable";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons";
import { getSurveyTags } from "../SurveyHelpers/surveyHelpers";
import { useNavigate } from "react-router";
import { sortItemsByAttribute, sortItemsByDate } from "../api";

const CompletedSurveys = ({ surveys, filtering }): JSX.Element => {
  const navigate = useNavigate();
  const [sortedSurveys, setSortedSurveys] = useState(surveys);
  const [sortBy, setSortBy] = useState({
    name: "",
    project: "",
    completedBy: "",
    completedOn: "",
  });

  useEffect(() => {
    if (surveys.data) {
      setSortedSurveys(surveys);
    }
  }, [surveys]);

  const columnHeaders = [
    {
      label: "Survey Name",
      width: 3,
      sorted: sortBy.name,
      sortable: true,
    },
    {
      label: "Related Project",
      width: 3,
      sorted: sortBy.project,
      sortable: true,
    },
    {
      label: "Completed By",
      width: 2,
      sorted: sortBy.completedBy,
      sortable: true,
    },
    {
      label: "Completed On",
      width: 2,
      sorted: sortBy.completedOn,
      sortable: true,
    },
    {
      label: "Tags",
      width: 2,
      sorted: "",
      sortable: false,
    },
  ];

  interface CompletedSurvey {
    id: string;
    checked: boolean;
    attributes: {
      name: JSX.Element;
      project: JSX.Element;
      completedBy: string;
      completedOn: string;
      tags: JSX.Element;
    };
  }

  const tableItems: CompletedSurvey[] = [];
  sortedSurveys.data.forEach((survey, i) => {
    const surveyName = (
      <span
        className="surveyName"
        onClick={
          survey.attributes.questionnaireId
            ? () =>
                navigate(`/surveys/${survey.attributes.questionnaireId}/take`, {
                  state: { surveyId: survey.id },
                })
            : () =>
                alert(
                  "This survey does not have any questions attached to it. Please go to settings to configure this survey's questions."
                )
        }
      >
        {survey.attributes.name}
      </span>
    );

    const relatedProject =
      survey.attributes.projectId !== "" ? (
        <div className="relatedProject">
          <FontAwesomeIcon
            icon={faArrowUpRightFromSquare}
            onClick={() =>
              window.open(
                `/projects/${survey.attributes.projectId}/edit`,
                "_blank"
              )
            }
            className="previewProject"
          />
          <span
            onClick={() =>
              window.open(
                `/projects/${survey.attributes.projectId}/edit`,
                "_blank"
              )
            }
          >
            {survey.attributes.project}
          </span>
        </div>
      ) : (
        <div>{survey.attributes.project}</div>
      );

    const tags = getSurveyTags(survey.attributes.tags);
    const result = {
      id: survey.id,
      checked: survey.checked,
      attributes: {
        name: surveyName,
        project: relatedProject,
        completedBy: survey.attributes.completedBy,
        completedOn: survey.attributes.completedOn,
        tags: tags,
      },
    };

    tableItems.push(result);
  });

  const handleSort = (column) => {
    const split = column.split(" ");
    let name = split[split.length - 1];
    if (split[0] === "Completed") {
      name = split[0].toLowerCase() + name;
    } else {
      name = name.toLowerCase();
    }

    const sorted = sortItemsByAttribute(
      name === "completedOn" ? null : sortedSurveys,
      name,
      sortBy
    );

    if (name !== "completedOn") {
      setSortedSurveys(sorted.data);
    } else {
      if (sorted.columns.completedOn !== "") {
        setSortedSurveys({
          ...surveys,
          data: sortItemsByDate(surveys.data, name, sortBy),
        });
      }
    }
    setSortBy(sorted.columns);
  };

  return !surveys ? (
    <SmallSpinner />
  ) : (
    <>
      <FEPaginatedTable
        tableType="surveys"
        tableItemsData={{
          ...sortedSurveys,
          data: tableItems,
        }}
        columnHeaders={columnHeaders}
        checkboxes={false}
        filtered={filtering}
        checkedItems={[]}
        setCheckedItems={() => null}
        onSort={handleSort}
        pageSizeEnabled
      />
    </>
  );
};

export default CompletedSurveys;
